import React, { ReactElement, useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { PERSISTENCE_KEYS } from 'common/constants/persistence.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { selectedCounterpartyAtom } from 'domains/accounts/store/selected-counterparty';
import { useGetBranchesMultiRoleQuery_MultiRole } from 'domains/branches/queries/branch.query';
import {
  TCounterpartyAvailableBranchesResData,
  TCounterpartyBranchesResData,
} from 'domains/branches/types/branch.types';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import {
  TCounterpartiesListReqParams,
  TCounterpartiesListResData,
} from 'domains/counterparties/types/counterparty.types';
import { useGetCounterpartyCustomersListQuery_MultiRole } from 'domains/customers/queries/customers.query';
import {
  TAccountManagementCustomersPageLoaderData,
  TCustomersListReqParams,
} from 'domains/customers/types/customers.types';
import { TScopeResData } from 'domains/roles/types/roles.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { ExpandIconRenderComponent } from './components/expand-icon-render.component';
import { ExpandedRowContentComponent } from './components/expanded-row-content.component';
import { SFiltersContainer } from './customers.page.styles';
import {
  EAccountManagementCustomersColumnKeys,
  EAccountManagementCustomersPageGetParams,
  EAccountManagementCustomersPageGetParamsActionTypes,
  EModalType,
} from './enums/customers.enum';
import CreateEditCustomersModal from './modals/create-edit-customers.modal';
import { getColumns } from './table/table.columns';
import { TCustomersAccountRecord, TCustomersModalData } from './types/customers.types';

type TPaginationConfig = {
  page: number;
  pageSize: number;
};

enum EFilterForm {
  SearchString = 'search_string',
  Counterparty = 'counterparty_guid',
  Branch = 'branch_name_guid',
}

const AccountManagementViewCustomersPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const loaderData = useLoaderData() as TAccountManagementCustomersPageLoaderData;
  const [paginationData, setPaginationData] = useState<TPaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [modalData, setModalData] = useState<TCustomersModalData | {}>({});
  const [filtersData, setFiltersData] = useState<{
    selectedBranch: string;
    selectedCounterparty: string;
    selectedSearchString: string;
  }>({
    selectedBranch: (loaderData.branchesData as TCounterpartyAvailableBranchesResData).branches?.[0].guid || '',
    selectedCounterparty: ENV_COUNTERPARTY_GUID,
    selectedSearchString: '',
  });
  const [selectedCounterparty, setSelectedCounterparty] = useAtom(selectedCounterpartyAtom);

  useEffect(() => {
    if (!selectedCounterparty) {
      setSelectedCounterparty(IS_ADMIN_FE ? loaderData.defaultSelectedCounterparty : ENV_COUNTERPARTY_GUID);
    }

    form.setFields([
      {
        name: EFilterForm.Counterparty,
        value: IS_ADMIN_FE ? loaderData.defaultSelectedCounterparty : ENV_COUNTERPARTY_GUID,
      },
    ]);
  }, []);

  const {
    data: counterpartiesData,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {},
    {
      refetchOnMount: false,
      enabled: IS_ADMIN_FE,
    },
  );

  const {
    data: branchesData,
    isLoading: isBranchesDataLoading,
    isError: isBranchesDataError,
    error: branchesDataError,
  } = useGetBranchesMultiRoleQuery_MultiRole(selectedCounterparty, {}, loaderData.hasAccessForAllBranches, {
    refetchOnMount: false,
    select: (data: any) => {
      const redoData = {
        ...data,
        ...(loaderData.hasAccessForAllBranches ? { branches: data.list } : null),
      };

      return redoData;
    },
  });

  const {
    data: customersData,
    isLoading: isCustomersDataLoading,
    isError: isCustomersDataError,
    error: customersDataError,
  } = useGetCounterpartyCustomersListQuery_MultiRole(
    selectedCounterparty,
    filtersData.selectedBranch,
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      search_string: filtersData.selectedSearchString,
    },
    loaderData.hasAccessForAllBranches,
    {
      refetchOnMount: false,
      placeholderData: keepPreviousData,
    },
  );

  const isError = isBranchesDataError || isCustomersDataError || isCounterpartiesDataError;
  const error = branchesDataError || customersDataError || counterpartiesDataError;

  useEffect(() => {
    if (isError) {
      console.error(error);
    }
  }, [isError, error]);

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EAccountManagementCustomersPageGetParams.Action);

    if (pageAction === EAccountManagementCustomersPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: {},
      });
    }
  }, [document.location.search]);

  const counterpartyOptions = (counterpartiesData?.counterparties || [])
    .map((c) => ({
      label: c.name,
      value: c.guid,
    }))
    .filter((counterparty) =>
      IS_ADMIN_FE ? counterparty.value !== ENV_COUNTERPARTY_GUID : counterparty.value === ENV_COUNTERPARTY_GUID,
    );

  const customersTableData: TCustomersAccountRecord[] = (customersData?.customers || []).map((item) => ({
    ...item,
    external_id: item?.login,
    accounts: 5, // mock
    currencies: ['UAH', 'USD', 'EUR'], // mock
  }));

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_CUSTOMERS);
  };

  const columns = getColumns();

  const handleCounterpartiesPaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const paginationConfig = {
    style: {
      marginRight: 30,
    },
    showQuickJumper: true,
    showSizeChanger: true,
    total: customersData?.total || 0,
    pageSize: paginationData.pageSize,
    current: paginationData.page,
    onChange: handleCounterpartiesPaginationChange,
  };

  const onChangeFiltersFormHandler = (values: Record<EFilterForm, string>): void => {
    const { counterparty_guid } = values;

    if (counterparty_guid) {
      setSelectedCounterparty(counterparty_guid);
      form.setFieldsValue({
        [EFilterForm.Branch]: null,
      });
    }
  };

  const onSubmitFiltersFormHandler = (values: Record<EFilterForm, string>): void => {
    const { search_string, counterparty_guid, branch_name_guid } = values;

    setFiltersData({
      selectedBranch: branch_name_guid,
      selectedCounterparty: IS_ADMIN_FE ? counterparty_guid : selectedCounterparty,
      selectedSearchString: search_string,
    });
  };

  const expandedRowRender = (record: TCustomersAccountRecord): ReactElement => (
    <ExpandedRowContentComponent record={record} counterparty={filtersData.selectedCounterparty} />
  );

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmitFiltersFormHandler}
        onValuesChange={onChangeFiltersFormHandler}
      >
        <SFiltersContainer>
          {IS_ADMIN_FE && (
            <Form.Item name={EFilterForm.Counterparty}>
              <Select style={{ width: 320 }} loading={isBranchesDataLoading} options={counterpartyOptions} />
            </Form.Item>
          )}
          <Form.Item name={EFilterForm.Branch}>
            <Select
              style={{ width: 320 }}
              placeholder={t('accountManagement.customers.filterButtonBranch.placeholder') as string}
              allowClear
            >
              {((branchesData as TCounterpartyAvailableBranchesResData)?.branches || []).map((option) => (
                <Select.Option key={option.guid} value={option.guid}>
                  {option?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={EFilterForm.SearchString}>
            <Input
              style={{ width: 320 }}
              placeholder={t('accountManagement.customers.searchString.placeholder')!}
              allowClear
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={isCustomersDataLoading} style={{ width: 120 }}>
            {t('accountManagement.customers.filterButton.placeholder')}
          </Button>
        </SFiltersContainer>
      </Form>
      <Table
        columns={columns}
        rowKey={EAccountManagementCustomersColumnKeys.ExternalID}
        pagination={paginationConfig}
        dataSource={customersTableData}
        expandable={{
          expandedRowRender: (record) => expandedRowRender(record),
          defaultExpandedRowKeys: ['0'],
          expandIcon: ExpandIconRenderComponent,
        }}
        loading={isCustomersDataLoading}
      />
      <CreateEditCustomersModal isOpen={!!Object.keys(modalData).length} onCancelModalHandler={onCancelModalHandler} />
    </div>
  );
};

export const loader = async (): Promise<{
  customersData: TCustomersListReqParams | {};
  counterpartiesData: TCounterpartiesListReqParams | {};
  branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData | {};
  hasAccessForAllBranches: boolean;
  defaultSelectedCounterparty: string;
}> => {
  try {
    let counterpartiesData: TCounterpartiesListReqParams = {};

    if (IS_ADMIN_FE) {
      const counterpartiesDataQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
      counterpartiesData =
        queryClient.getQueryData(counterpartiesDataQuery.queryKey) ??
        (await queryClient.fetchQuery(counterpartiesDataQuery));
    }

    const filteredCounterpartyList = (counterpartiesData as TCounterpartiesListResData).counterparties.filter(
      ({ guid }) => guid !== ENV_COUNTERPARTY_GUID,
    );

    const selectCounterpartyFromLocalStore = localStorage.getItem(
      PERSISTENCE_KEYS.ACCOUNT_MANAGEMENT_SELECTED_COUNTERPARTY,
    );

    const checkedCounterpartyGuid = filteredCounterpartyList.find(
      (item) => item.guid === selectCounterpartyFromLocalStore,
    )?.guid;

    const defaultSelectedCounterparty =
      checkedCounterpartyGuid || filteredCounterpartyList[filteredCounterpartyList.length - 1].guid;

    const queryScopeListData =
      queryKeysClient[EQueryKeys.RbacQueryKeys].getCounterpartyScopesList(ENV_COUNTERPARTY_GUID);
    const scopeListData: TScopeResData =
      queryClient.getQueryData(queryScopeListData.queryKey) ?? (await queryClient.fetchQuery(queryScopeListData));

    const hasAccessForAllBranches =
      ((scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'employees')?.is_allowed &&
        (scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'branches')?.is_allowed) ||
      false;

    const getBranchesMultiRoleQuery = queryKeysClient[EQueryKeys.BranchQueryKeys].getBranches_MultiRole(
      ENV_COUNTERPARTY_GUID,
      {},
      hasAccessForAllBranches,
    );
    const branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData =
      queryClient.getQueryData(getBranchesMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getBranchesMultiRoleQuery));

    const defaultBranchGUID = hasAccessForAllBranches
      ? ''
      : (branchesData as TCounterpartyAvailableBranchesResData).branches[0].guid;

    const getCustomersMultiRoleQuery = queryKeysClient[EQueryKeys.CustomerQueryKeys].getCustomersList_MultiRole(
      ENV_COUNTERPARTY_GUID,
      defaultBranchGUID,
      {
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
        search_string: '',
      },
      hasAccessForAllBranches,
    );
    const customersData: TCustomersListReqParams =
      queryClient.getQueryData(getCustomersMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getCustomersMultiRoleQuery));

    return {
      customersData,
      branchesData,
      counterpartiesData,
      hasAccessForAllBranches,
      defaultSelectedCounterparty,
    };
  } catch (error) {
    console.error(error);
    return {
      hasAccessForAllBranches: false,
      counterpartiesData: {},
      defaultSelectedCounterparty: '',
      customersData: {},
      branchesData: {},
    };
  }
};

export default AccountManagementViewCustomersPage;
